import { useState, useMemo, useRef, useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { ChevronsUpDown, Copy, GripVertical, PencilIcon, Plus, X } from "lucide-react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import useResizeObserver from "../useResizeObserver";
import Container from "../board/dnd/Container";
import Draggable from "../board/dnd/Draggable";
import LayoutAction from "../actions/LayoutAction";

export const ChipDomains = ({ type, domains, showRemove, onRemove }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [containerDomainRef, size]: any = useResizeObserver();

  const visibleCount = useMemo(() => {
    if (domains.length === 1) return 1;

    const gap = 4; // gap-1
    const estimateChipWidth = (text) => {
      if (type === "FORM") {
        return text.length * 7 + gap * 4 + (showRemove ? 16 : 0);
      }
      return text.length * 5 + gap * 4 + (showRemove ? 16 : 0);
    };

    let width = size.width;
    if (type === "INLINE") {
      width = width - 48; // right icon
    } else if (type === "FORM") {
      width = width - 48; // right icon
    } else if (type === "GRID") {
      width = width - 40;
    }
    const viewAllWidth = estimateChipWidth(`+${domains.length}`);
    let used = 0;
    let count = 0;

    for (let i = 0; i < domains.length; i++) {
      const chipWidth = estimateChipWidth(domains[i]);
      const next = used + chipWidth + gap + viewAllWidth;
      if (next > width) break;
      used += chipWidth + gap;
      count++;
    }

    return count;
  }, [showRemove, type, domains.length, size.width]); // eslint-disable-line react-hooks/exhaustive-deps

  const titleDomains = domains.slice(visibleCount, domains.length).join("\n");

  let chipClass = "px-2 py-0.5 text-xs font-medium leading-4";
  if (type === "INLINE") {
  } else if (type === "FORM") {
    chipClass = "px-2 py-1.5 text-sm font-medium leading-4";
  } else if (type === "GRID") {
  }

  return (
    <div ref={containerDomainRef} className="w-full flex flex-nowrap gap-1 overflow-hidden">
      {domains.slice(0, visibleCount).map((domain) => {
        return (
          <div
            key={domain}
            title={domain}
            className={`shrink-0 flex items-center gap-1 rounded-lg ${chipClass} text-gray-600 bg-gray-100 cursor-pointer`}
          >
            <div
              className="group/domain relative text-blue-600"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                window.open(`https://${domain}`, "_blank");
              }}
            >
              {domain}
              {!showRemove ? (
                <Copy
                  className="w-4 h-4 absolute -right-2 top-1/2 -translate-y-1/2 
                    opacity-0 pointer-events-none transition-opacity duration-200 group-hover/domain:opacity-100 group-hover/domain:pointer-events-auto 
                    bg-gray-100 pl-1 text-gray-600 cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    navigator.clipboard.writeText(domain);
                    dispatch(
                      LayoutAction.setNotification({
                        title: t("Info"),
                        type: "success",
                        message: t("Copied!"),
                      })
                    );
                  }}
                />
              ) : null}
            </div>
            {showRemove ? (
              <X
                className="w-3 h-3 text-gray-600"
                onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  onRemove(domain);
                }}
              />
            ) : (
              <div></div>
            )}
          </div>
        );
      })}
      {visibleCount < domains.length && (
        <button
          title={titleDomains}
          className={`shrink-0 flex items-center gap-1 rounded-full ${chipClass} cursor-pointer bg-gray-100 text-gray-600`}
        >
          +{domains.length - visibleCount}
        </button>
      )}
    </div>
  );
};

interface DomainsSelectProps {
  type: "INLINE" | "FORM" | "GRID";
  value: string[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  showRemove?: boolean;
  testId?: string;
}

const DomainsSelect = ({
  type,
  value,
  onChange,
  placeholder,
  showRemove = false,
  testId,
}: DomainsSelectProps) => {
  const { t } = useTranslation();

  const popoverButtonRef = useRef(null);
  const popoverPanelRef = useRef(null);
  const [panelWidth, setPanelWidth] = useState(null);

  const [newDomain, setNewDomain] = useState("");

  const formatDomain = (domain: string) => {
    let cleanDomain = domain.replace(/^https?:\/\//, "");
    cleanDomain = cleanDomain.replace(/^www\./, "");
    cleanDomain = cleanDomain.replace(/\/$/, "");
    return cleanDomain.trim().toLowerCase();
  };

  const validateDomain = (domain: string) => {
    if (!domain.trim()) return false;
    const domainRegex =
      /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return domainRegex.test(formatDomain(domain));
  };

  const onDrop = (dropResult) => {
    const { removedIndex, addedIndex } = dropResult;
    if (removedIndex === null || addedIndex === null) return;

    const updated = [...value];
    const [moved] = updated.splice(removedIndex, 1);
    updated.splice(addedIndex, 0, moved);

    onChange([...updated]);
  };

  const handleAdd = () => {
    if (!newDomain || !validateDomain(newDomain)) return;
    setNewDomain("");
    onChange([...value, newDomain]);
  };

  const handleRemove = (domain: string) => {
    const newDomainList = value.filter((val) => val !== domain);
    onChange([...newDomainList]);
  };

  const renderDomainList = () => {
    return (
      <div>
        <Container
          onDrop={onDrop}
          dragHandleSelector={".drag-handle"}
          nonDragAreaSelector={".no-drag"}
        >
          {value.map((item, index) => {
            return (
              <Draggable key={`${item}_${index}`}>
                <div className="w-full flex items-center justify-between space-x-2">
                  <div className="flex items-center gap-2">
                    <div className="drag-handle cursor-move">
                      <GripVertical className="w-4 h-4" />
                    </div>
                    <div className="no-drag">
                      <a
                        href={`https://${item}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm text-blue-600"
                      >
                        {item}
                      </a>
                    </div>
                  </div>
                  <div className="no-drag p-1 flex items-center rounded-md hover:bg-gray-100">
                    <X
                      className={"w-5 h-5 text-gray-400 cursor-pointer"}
                      onClick={() => {
                        handleRemove(item);
                      }}
                    />
                  </div>
                </div>
              </Draggable>
            );
          })}
        </Container>
      </div>
    );
  };

  const renderAdd = () => {
    return (
      <div className="flex items-center gap-2">
        <Plus className="w-4 h-4 text-gray-600" />
        <input
          data-testid={testId ? `${testId}-input` : undefined}
          autoFocus
          className="w-full rounded-md border border-gray-300 px-2 py-1 text-sm outline-none"
          placeholder={t("Add domain...")}
          value={newDomain}
          onChange={(e) => {
            setNewDomain(e.target.value);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              handleAdd();
            }
          }}
        />
        <button
          data-testid={testId ? `${testId}-add-button` : undefined}
          type="button"
          disabled={!newDomain || !validateDomain(newDomain)}
          className="px-2 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-500 disabled:bg-gray-300 disabled:text-gray-600"
          onClick={handleAdd}
        >
          {t("Add")}
        </button>
      </div>
    );
  };

  const renderTarget = (isOpen) => {
    if (type === "INLINE") {
      return (
        <div className="group w-full flex items-center justify-between rounded-md transition-colors duration-150 hover:bg-gray-50 flex-1 min-h-[28px] cursor-pointer">
          {value.length > 0 ? (
            <ChipDomains
              type={type}
              domains={value}
              showRemove={showRemove || isOpen}
              onRemove={handleRemove}
            />
          ) : (
            <div className="w-full text-left font-medium text-xs leading-4 text-gray-400">
              {placeholder}
            </div>
          )}
          <div className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
            <PencilIcon className="w-3 h-3 text-gray-400 mr-2" />
          </div>
        </div>
      );
    } else if (type === "FORM") {
      return (
        <div
          className={`w-full h-9.5 flex items-center justify-between overflow-hidden gap-2 px-3 cursor-default rounded-lg ${
            isOpen ? "ring-4 ring-blue-100 border border-blue-600" : "border border-gray-200"
          }`}
        >
          {value.length > 0 ? (
            <ChipDomains
              type={type}
              domains={value}
              showRemove={showRemove || isOpen}
              onRemove={handleRemove}
            />
          ) : (
            <div className="flex items-center flex-nowrap overflow-hidden gap-2">
              <div className="text-sm leading-4 text-gray-500 truncate">{placeholder}</div>
            </div>
          )}
          <div className="shrink-0 flex items-center flex-nowrap gap-4">
            <ChevronsUpDown className="w-3.5 h-3.5 text-gray-400" />
          </div>
        </div>
      );
    } else if (type === "GRID") {
      return (
        <div className="w-full cursor-pointer">
          {value.length > 0 ? (
            <ChipDomains
              type={type}
              domains={value}
              showRemove={showRemove || isOpen}
              onRemove={handleRemove}
            />
          ) : (
            <div className="w-full text-left leading-4 text-gray-500 cursor-default">
              {placeholder}
            </div>
          )}
        </div>
      );
    }
    return null;
  };

  let popoverPanelClassName = "";
  if (type === "INLINE") {
    popoverPanelClassName = "-mt-6.5";
  } else if (type === "FORM") {
    popoverPanelClassName = "mt-2";
  } else if (type === "GRID") {
    popoverPanelClassName = "mt-1";
  }

  return (
    <>
      <Popover className="w-full">
        {({ open }) => {
          // eslint-disable-next-line react-hooks/rules-of-hooks
          useLayoutEffect(() => {
            if (!open) return;
            if (!popoverButtonRef.current || !popoverPanelRef.current) return;
            const buttonWidth = popoverButtonRef.current.offsetWidth;
            const panelNaturalWidth = popoverPanelRef.current.offsetWidth;
            setPanelWidth(Math.max(buttonWidth, panelNaturalWidth));
          }, [open]);

          return (
            <>
              <PopoverButton
                data-testid={testId}
                ref={popoverButtonRef}
                as="div"
                onClick={(e) => {
                  if (open) {
                    e.preventDefault();
                  }
                }}
              >
                {renderTarget(open)}
              </PopoverButton>
              <PopoverPanel
                ref={popoverPanelRef}
                anchor="bottom start"
                style={{ width: panelWidth ?? "auto" }}
                className={`z-9999 p-2 bg-white rounded-md border border-gray-300 shadow-xl ${popoverPanelClassName}`}
              >
                <div>
                  {value.length > 0 ? (
                    <>
                      <div className="w-full max-h-60 overflow-y-auto">{renderDomainList()}</div>
                      <div className="w-full my-2 border-b border-gray-200"></div>
                    </>
                  ) : null}
                  {renderAdd()}
                </div>
              </PopoverPanel>
            </>
          );
        }}
      </Popover>
    </>
  );
};

export default DomainsSelect;
